// ONE "REQUIRED AND EMPTY" RULE FOR THE WHOLE INVOICE MODAL (owner,
// 2026-09-25 — D-156): *"the red borders around required fields in the modal
// … the red was being applied inconsistently."*
//
// THE RULE, stated once: a box gets a red border when the field is REQUIRED
// AT THIS STEP, it is EMPTY, and the viewer CAN FILL IT IN HERE. A field the
// viewer cannot touch is never red — red on a control nobody can use is noise.
//
// Before this file, the header used Fluent `Field`s, the line cells used a
// local class, the pickers used `invalid`, and the Marketing Category
// field and the read-only table's Department were never wired at all. Every
// line control in `EditableInvoiceForm.tsx` and `InvoiceLineItemsTable.tsx`
// that can show red now reads its state from here, and so do the Additional
// Detail and Associated Entities icons.
//
// WHAT "REQUIRED AT THIS STEP" MEANS mirrors the server's refusals exactly:
//  - Import Control's release: `missingRequiredFields`
//    (`backend/src/domain/requiredFields.ts`, D-128) — description, price,
//    quantity, account, department (since D-309; optional from D-217 until
//    then), and the Marketing Category (D-190). Nothing for transportation:
//    Fleet Maintenance's detail is read-only and never owed (D-381).
//    The release was AP/AR Approval's until that step merged into Import
//    Control (D-215).
//  - Approval at the approval tiers and completion at Manual Entry: the coding
//    gate in `routes/decisions.ts` (D-9) — account, department, and the
//    Marketing Category (`line_item_without_marketing_category`). Controller
//    Approval is held to it here too: both halves lock after it (D-305), so a
//    line it lets through uncoded could never be coded again.
//  - Export Control: the export refuses a line without both halves
//    (`exportBuilder.ts`), so account and department.
//  - An unmatched tagged entity: Import Control's release, through its flag.
//
// RED IS ONLY THIS (owner, 2026-09-30 — D-248). A flag never turns a field red.
// A value tied to an unresolved flag — a low-confidence Vendor, amounts that do
// not reconcile — is YELLOW, and `./flagged.ts` owns which flag ties to which
// field. Red beats yellow on a field that is both.

import { glCodeHalfOpen, type AdditionalDetailSections } from '@ap/shared'
import { DecisionStep, InvoiceStatus } from './constants'
import type { LineItemDetailScreen } from './format'

/** Empty, for every value a required box can hold. Whitespace is empty; zero is not. */
export function isBlank(value: unknown): boolean {
  return value === null || value === undefined || String(value).trim() === ''
}

/** Every line-level value that can be required somewhere. */
export type RequiredLineField =
  | 'description'
  | 'price'
  | 'quantity'
  | 'account'
  | 'department'
  | 'marketingCategory'
  /** A tagged Vehicle or Staff entity left unmatched (`UnmatchedTaggedEntity`). */
  | 'associatedEntity'

const TIERS = [
  DecisionStep.Controller,
  DecisionStep.Department,
  DecisionStep.Director,
  DecisionStep.Oversight,
] as const

/** Where each field is required — the server's refusals, step by step. */
export const REQUIRED_AT: Readonly<Record<RequiredLineField, ReadonlySet<LineItemDetailScreen>>> = {
  description: new Set([DecisionStep.ImportControl]),
  price: new Set([DecisionStep.ImportControl]),
  quantity: new Set([DecisionStep.ImportControl]),
  account: new Set([DecisionStep.ImportControl, ...TIERS, DecisionStep.ExportControl]),
  // Import Control too since D-309 (owner, 2026-10-07): *"Manager Approval is
  // no longer the step for assigning the Department; that is to happen now at
  // Import Control."* From D-217 until then it was the one field there that was
  // not required.
  department: new Set([DecisionStep.ImportControl, ...TIERS, DecisionStep.ExportControl]),
  // Export Control joined with D-191: it can recode a line onto a Marketing
  // account, and the export then refuses the line until its category is in.
  marketingCategory: new Set([DecisionStep.ImportControl, ...TIERS, DecisionStep.ExportControl]),
  associatedEntity: new Set([DecisionStep.ImportControl]),
}

export function requiredAt(field: RequiredLineField, screen: LineItemDetailScreen): boolean {
  return REQUIRED_AT[field].has(screen)
}

/** THE RED-BORDER RULE. Required at this step, empty, and fillable here. */
export function showRequiredBorder(
  field: RequiredLineField,
  screen: LineItemDetailScreen,
  empty: boolean,
  editableHere: boolean,
): boolean {
  return editableHere && empty && requiredAt(field, screen)
}

/**
 * WHERE THE MARKETING CATEGORY IS EDITABLE AT INTAKE — the server's
 * `canEditLineItemDetail` grant: Import Control (D-190; there since AP/AR
 * Approval merged into it, D-216). `marketingCategoryEditableAt` adds the
 * later steps. Elsewhere the pop-up shows the category as plain text. A hold
 * changes nothing here (D-282). Fleet Maintenance's detail is never editable.
 */
export function additionalDetailEditableAt(screen: LineItemDetailScreen): boolean {
  return screen === DecisionStep.ImportControl
}

// ---- Where the GL code can change (D-305, D-308, D-309) ---------------------
//
// THE OWNER'S RULES (2026-10-07), as the server applies them
// (`glEditAuthorityForStatus` in `backend/src/domain/authority.ts`); WHERE is
// one shared function both sides call (`glCodeHalfOpen`,
// `shared/src/constants.ts`), so no screen offers a picker the server would
// refuse. Both halves answer the same, on every route:
//
//   Import Control                      Account and Department, both required (D-309)
//   Controller Approval                 Account and Department, both required
//   Export Control                      Account and Department (D-308)
//   Manual Entry                        Account and Department (D-309)
//   every other step                    neither

/** Which halves of a line's GL code this step may change. */
interface GlCodeEditable {
  account: boolean
  department: boolean
}

/** The queue status a screen decides from; Closing Confirmation's is
    `Pending Export`, and Archives decides nothing. */
function statusForScreen(screen: LineItemDetailScreen): string | null {
  if (screen === 'archives') return null
  if (screen === DecisionStep.ClosingConfirmation) return InvoiceStatus.PendingExport
  return `Pending ${screen}`
}

/** The rule above for one step — the SAME rule the server applies. */
export function glCodeEditable(screen: LineItemDetailScreen): GlCodeEditable {
  const status = statusForScreen(screen)
  const open = status !== null && glCodeHalfOpen(status)
  return { account: open, department: open }
}

/**
 * The steps after Import Control that can change a line's Account (the server's
 * `glEditAuthorityForStatus`): Controller Approval since D-305 and Export
 * Control since D-308 (and Manual Entry from D-309 until the step was dropped,
 * D-311). The Marketing Category is editable
 * there, because it is *"downstream of an Account"* (owner, 2026-09-28 — D-192).
 */
export const LATE_MARKETING_STEPS: ReadonlySet<LineItemDetailScreen> = new Set([
  DecisionStep.Controller,
  DecisionStep.ExportControl,
])

/** Is the Marketing Category editable here? Wherever the Account is (D-192). */
export function marketingCategoryEditableAt(screen: LineItemDetailScreen): boolean {
  return additionalDetailEditableAt(screen) || LATE_MARKETING_STEPS.has(screen)
}

/** Could a category picker be offered on this screen at all? The same steps. */
export function categoryPickableAt(screen: LineItemDetailScreen): boolean {
  return marketingCategoryEditableAt(screen)
}

// Which Additional Detail a line shows is `additionalDetailSections` in
// `@ap/shared`, the rule the server applies (D-190, D-381; code review D-04).
// The same answer on every screen: the icon shows on every line wherever it
// sits, and lights up for a line that has something (owner, 2026-09-28).

/** One line, as Import Control's release gate reads it. */
interface RequiredLineInput {
  description: string | null | undefined
  price: string | number | null | undefined
  quantity: string | number | null | undefined
  accountId: string | null | undefined
  /** Required at release since D-309. */
  departmentId: string | null | undefined
  sections: AdditionalDetailSections
  marketingCategoryId: string | null | undefined
}

/** A line value Import Control's release refuses without, in the order the line reads
    left to right: the cells, then what the Additional Detail pop-up holds. */
export type ReleaseLineGap =
  | 'description'
  | 'price'
  | 'quantity'
  | 'account'
  | 'department'
  | 'marketingCategory'

/**
 * IMPORT CONTROL'S RELEASE GATE FOR ONE LINE — the client's copy of
 * `missingRequiredFields`' line clause, as the list of what is empty. The
 * server is the gate; this is what flattens the Release button before the
 * click AND what its tooltip names (QA C1-01, 2026-09-25: an empty
 * Description greyed Release with no reason given). The Department is required
 * here since D-309; it was optional from D-217 until then.
 */
export function lineRequiredGaps(line: RequiredLineInput): ReleaseLineGap[] {
  const gaps: ReleaseLineGap[] = []
  if (isBlank(line.description)) gaps.push('description')
  if (isBlank(line.price)) gaps.push('price')
  if (isBlank(line.quantity)) gaps.push('quantity')
  if (isBlank(line.accountId)) gaps.push('account')
  if (isBlank(line.departmentId)) gaps.push('department')
  if (line.sections.marketing && isBlank(line.marketingCategoryId)) gaps.push('marketingCategory')
  return gaps
}

/** "line 3", "lines 1 and 3", "lines 1, 2 and 4" — the numbers in the table's # column. */
export function lineNumbersPhrase(numbers: readonly number[]): string {
  const sorted = [...numbers].sort((a, b) => a - b).map(String)
  if (sorted.length === 1) return `line ${sorted[0]}`
  return `lines ${sorted.slice(0, -1).join(', ')} and ${sorted[sorted.length - 1]}`
}

/** What to do about each gap, worded for the control that fixes it. */
const LINE_GAP_ACTION: Readonly<Record<ReleaseLineGap, string>> = {
  description: 'Fill in “Description”',
  price: 'Fill in “Price”',
  quantity: 'Fill in “Quantity”',
  account: 'Set “Account”',
  department: 'Set “Department”',
  marketingCategory: 'Pick a “Marketing Category” under Additional Detail',
}

/**
 * ONE SENTENCE PER EMPTY LINE FIELD, naming the lines by the table's own
 * numbers — what the disabled Release button's tooltip says for the line
 * half of the gate. Every gap that holds, at once, in the order the fields
 * sit on the line. Empty when nothing on a line is missing.
 */
export function lineGapReasons(
  lines: readonly { lineNumber: number; gaps: readonly ReleaseLineGap[] }[],
  action = 'release',
): string[] {
  const order = Object.keys(LINE_GAP_ACTION) as ReleaseLineGap[]
  const out: string[] = []
  for (const gap of order) {
    const numbers = lines.filter((l) => l.gaps.includes(gap)).map((l) => l.lineNumber)
    if (numbers.length > 0) out.push(`${LINE_GAP_ACTION[gap]} on ${lineNumbersPhrase(numbers)} to ${action}.`)
  }
  return out
}

/** The header fields the release refuses without (Company is its own sentence). */
interface HeaderGaps {
  vendor: boolean
  externalId: boolean
  date: boolean
  tax: boolean
  shipping: boolean
  total: boolean
}

const HEADER_GAP_SENTENCE: Readonly<Record<keyof HeaderGaps, string>> = {
  vendor: 'Set the Vendor',
  externalId: 'Fill in the External ID',
  date: 'Set the Invoice Date',
  tax: 'Fill in Tax',
  shipping: 'Fill in Shipping',
  total: 'Fill in the Total',
}

/** One sentence per empty header field, top of Invoice Summary downwards. */
export function headerGapReasons(gaps: HeaderGaps, action = 'release'): string[] {
  return (Object.keys(HEADER_GAP_SENTENCE) as (keyof HeaderGaps)[])
    .filter((key) => gaps[key])
    .map((key) => `${HEADER_GAP_SENTENCE[key]} to ${action}.`)
}

/**
 * THE CODING GATE'S REASON — Approve at the three tiers, Typed
 * Into Point Click Care at Manual Entry (D-9). Those buttons read the
 * server's own counts (`uncoded_line_item_count`,
 * `capital_equipment_under_floor_count`, `missing_marketing_category_count`)
 * rather than the lines, because the queue row carries the counts and not the
 * lines; the fields they count are the ones `REQUIRED_AT` lists for those
 * steps. EVERY cause that holds is named, in one sentence, then one line of
 * advice (QA C1-01: a button's tooltip names every reason it is disabled).
 *
 * `advice` is the closing sentence, which differs by where the button is:
 * a queue row says to open the invoice first; the modal's own button does
 * not. `glOnly` picks "GL coding" when the Marketing Category is not among
 * the causes — telling a reviewer to fix the GL coding of a line whose
 * account and department are both set sends them hunting for a filled field.
 */
export function codingGateReason(
  counts: { missing: number; capital: number; marketing: number },
  advice: (glOnly: boolean) => string,
  /** Whole sentences naming a cause more exactly, placed before the advice —
      `inactiveAccountReasons` below, where the lines are loaded (D-308). */
  detail: readonly string[] = [],
): string | undefined {
  const { missing, capital, marketing } = counts
  if (missing <= 0 && capital <= 0 && marketing <= 0) {
    // A line on an Inactive Account where the Account can still change is a
    // cause on its own (D-308): it is fully coded, so no count names it.
    return detail.length === 0 ? undefined : [...detail, advice(true)].join(' ')
  }
  const parts: string[] = []
  if (missing > 0) {
    parts.push(
      missing === 1
        ? 'One line item still needs “Account” or “Department”'
        : `${missing} line items still need “Account” or “Department”`,
    )
  }
  if (capital > 0) {
    parts.push(
      capital === 1
        ? 'one line item is coded Capital Equipment but is under the $5,000 minimum — pick a different “Account” for it'
        : `${capital} line items are coded Capital Equipment but are under the $5,000 minimum — pick a different “Account” for them`,
    )
  }
  // Past Import Control this arises only on a line recoded onto a Marketing account,
  // and the step that recoded it can enter the category too (D-192).
  if (marketing > 0) {
    parts.push(
      marketing === 1
        ? 'one line item still needs a “Marketing Category”'
        : `${marketing} line items still need a “Marketing Category”`,
    )
  }
  // The later clauses are written lower-case to follow ", and"; whichever
  // leads is capitalized, so a sentence never starts "one line item…".
  const sentence = parts.join(', and ')
  return [`${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`, ...detail, advice(marketing <= 0)].join(' ')
}

/**
 * A LINE ON AN INACTIVE ACCOUNT (owner, 2026-10-07 — D-308): "If an Account
 * goes inactive while an invoice is at a step where the Account is editable,
 * then it must be changed. If an Account goes inactive while an invoice's
 * Account value is locked, then it proceeds as-is."
 *
 * So where the Account is editable — Import Control and Controller Approval
 * (`glCodeEditable`) — every line holding an Inactive Account blocks Release or
 * Approve until it is changed, with or without a Department; the server
 * refuses it as `line_item_inactive_account`. Where the Account is locked
 * nothing is said: a line with no Department can still take one, because the
 * Department picker offers the Inactive Account's own Codes (D-308), and the
 * invoice proceeds. This replaces D-175's narrower rule, which named only a
 * line with no Department and only at the later steps.
 */
interface InactiveAccountLine {
  lineNumber: number
  accountId: string
  accountName: string | undefined
}

export function linesOnInactiveAccount(
  lines: readonly { lineNumber: number; accountId: string | null | undefined }[],
  accountInactive: (accountId: string) => boolean,
  accountName: (accountId: string) => string | undefined,
): InactiveAccountLine[] {
  return lines
    .filter((l) => !isBlank(l.accountId) && accountInactive(String(l.accountId)))
    .map((l) => ({ lineNumber: l.lineNumber, accountId: String(l.accountId), accountName: accountName(String(l.accountId)) }))
}

/** One sentence per Inactive Account, naming it and its lines. */
export function inactiveAccountReasons(stranded: readonly InactiveAccountLine[]): string[] {
  const byAccount = new Map<string, InactiveAccountLine[]>()
  for (const line of stranded) byAccount.set(line.accountId, [...(byAccount.get(line.accountId) ?? []), line])
  return [...byAccount.values()].map((lines) => {
    const first = lines[0]!
    const named =
      first.accountName && first.accountName !== first.accountId
        ? `Account ${first.accountId} (${first.accountName})`
        : `Account ${first.accountId}`
    const where = lineNumbersPhrase(lines.map((l) => l.lineNumber))
    return `${where.charAt(0).toUpperCase()}${where.slice(1)} ${lines.length === 1 ? 'holds' : 'hold'} ${named}, which is Inactive — pick a different “Account”.`
  })
}

/** What a line icon shows: blue when it holds something, a red border when a
    required value inside it is empty and fillable here. */
export interface LineIconState {
  filled: boolean
  requiredMissing: boolean
}

export interface AdditionalDetailState extends LineIconState {
  marketingInvalid: boolean
}

/**
 * The Additional Detail icon's two states, and the red on each field inside
 * the pop-up, from the one rule above.
 *
 * BLUE MEANS SOMETHING IS THERE (owner, 2026-09-28): a Marketing Category set
 * on a line that owes one, or Fleet Maintenance's detail on a line that
 * carries it (D-381). A category left over on a line that no longer
 * qualifies — its Account changed off Marketing, say — does not light the
 * icon, because the line owes nothing. The same meaning blue has on the
 * Associated Entities icon beside it.
 *
 * NOTHING IS EVER RED FOR TRANSPORTATION: Fleet Maintenance's detail is
 * read-only and owed by no line.
 */
export function additionalDetailState(input: {
  sections: AdditionalDetailSections
  screen: LineItemDetailScreen
  editable: { marketing: boolean }
  marketingCategoryId: string | null | undefined
  /** The line carries Fleet Maintenance's detail (`transportation_detail` is set). */
  hasTransportationDetail: boolean
}): AdditionalDetailState {
  const { sections, screen, editable } = input
  const marketingInvalid =
    sections.marketing &&
    showRequiredBorder('marketingCategory', screen, isBlank(input.marketingCategoryId), editable.marketing)
  return {
    filled: (sections.marketing && !isBlank(input.marketingCategoryId)) || input.hasTransportationDetail,
    requiredMissing: marketingInvalid,
    marketingInvalid,
  }
}

/** The accessible name, carrying the icon's state so a screen reader hears it. */
export function lineItemIconLabel(label: string, filled: boolean, requiredEmpty: boolean): string {
  if (requiredEmpty) return `${label} — required field empty`
  if (filled) return `${label} — filled in`
  return label
}

/** The shape read off an `associated_entities` row. */
interface TagLike {
  entity_type?: string | null
  vehicle_id?: string | null
  staff_upn?: string | null
  is_active?: boolean
}

/**
 * The server's `isUnmatchedTag` (`backend/src/domain/taggedEntities.ts`): an
 * active Vehicle with no `vehicle_id`, or Staff with no `staff_upn`. A
 * Participant is never unmatched — nothing resolves one.
 */
export function isUnmatchedTag(row: TagLike): boolean {
  if (row.is_active === false) return false
  if (row.entity_type === 'Vehicle') return isBlank(row.vehicle_id)
  if (row.entity_type === 'Staff') return isBlank(row.staff_upn)
  return false
}

/**
 * The Associated Entities icon: blue with one or more active entities on the
 * line, a red border when one is unmatched and the viewer can fix it here
 * (the `UnmatchedTaggedEntity` flag blocks the release at Import Control).
 */
export function associatedEntitiesState(
  rows: readonly TagLike[],
  screen: LineItemDetailScreen,
  editableHere: boolean,
): LineIconState {
  const active = rows.filter((row) => row.is_active !== false)
  return {
    filled: active.length > 0,
    requiredMissing: showRequiredBorder('associatedEntity', screen, active.some(isUnmatchedTag), editableHere),
  }
}
